import {
  AdaptivePointsMaterial,
  type FetcherClient,
  type LodPointCloud,
  PotreeTree,
  WSKDTree,
} from "@faro-lotv/lotv";
import { Vector3 } from "three";
import { loadLodNode } from "@/utils/load-lod-node";
import type { SurfaceSelection } from "./surface-selection-pass";
import { SURFACE_TILE_SIZE, WorldSurface } from "./world-surface";
import {
  CylinderRefinement,
  cylinderSearchMargin,
} from "./cylinder-refinement";
import type { CylinderFitter } from "./cylinder-fitting";

const MAX_NODES = 256;
const MAX_POINTS = 8_000_000;
const MAX_DURATION = 30_000;
const POINTS_PER_BATCH = 8192;
const UPDATE_INTERVAL = 250;
const CYLINDER_LOOKAHEAD_TILES = 2;

/**
 * Grow a click preview through lazily loaded, surface-adjacent LOD tiles.
 *
 * @param cloud Source cloud and its shared loader.
 * @param seed Immediate click selection.
 * @param signal Cancellation on clear, replacement or source changes.
 * @param publish Progress callback for world-space selections.
 * @param fitCylinder Optional worker-backed cylinder fitter; unused for planes.
 */
export async function expandSurface(
  cloud: Pick<
    LodPointCloud,
    "lodTreeFetcher" | "tree" | "matrixWorld" | "material"
  >,
  seed: SurfaceSelection,
  signal: AbortSignal,
  publish: (selection: SurfaceSelection) => void,
  fitCylinder?: CylinderFitter,
): Promise<void> {
  let grid = new WorldSurface(seed);
  const refinement = seed.cylinder
    ? new CylinderRefinement(
        seed.cylinder,
        seed.origin,
        seed.tolerance,
        (diagnostic) => {
          if (
            ["unstable-fit", "residual", "insufficient-inliers"].includes(
              diagnostic.reason,
            )
          ) {
            partial = true;
          }
        },
        fitCylinder,
      )
    : undefined;
  const controller = new AbortController();
  function abort(): void {
    controller.abort();
  }
  function stopped(): boolean {
    return controller.signal.aborted;
  }
  function cancelled(): boolean {
    return signal.aborted;
  }
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) controller.abort();
  const timeout = setTimeout(abort, MAX_DURATION);
  const client: FetcherClient = {
    lodTreeFetcher: cloud.lodTreeFetcher,
    tree: cloud.tree,
  };
  const fetcher = client.lodTreeFetcher;
  const matrix = cloud.matrixWorld.clone();
  const inverse = matrix.clone().invert();
  const scale = matrix.getMaxScaleOnAxis();
  const { material } = cloud;
  const hidden = new Set(
    material instanceof AdaptivePointsMaterial ? material.hiddenScans : [],
  );
  const planes = material.clippingPlanes?.map((plane) => plane.clone()) ?? [];
  const point = new Vector3();
  const processed = new Set<number>();
  const requested = new Set<number>();
  let points = 0;
  let partial = false;
  function incomplete(): boolean {
    return partial || stopped() || grid.limited;
  }
  let lastUpdate = performance.now();
  fetcher.addClient(client);
  try {
    await grid.initialize(() => controller.signal.aborted);
    if (grid.seedTiles.size === 0 || grid.limited) {
      if (!signal.aborted) publish({ ...seed, coverage: "partial" });
      return;
    }
    let tiles: number[] = [];
    const pending = new Set<number>();
    const resolved = new Set<number>();
    const probed = new Set<number>();
    function enqueue(tile: number): void {
      if (pending.has(tile)) return;
      pending.add(tile);
      tiles.push(tile);
    }
    function enqueueSurface(tile: number): void {
      enqueue(tile);
      if (!refinement || !grid.seed.cylinder) return;
      const columns = grid.width / SURFACE_TILE_SIZE;
      const row = Math.floor(tile / columns);
      for (
        let offset = -CYLINDER_LOOKAHEAD_TILES;
        offset <= CYLINDER_LOOKAHEAD_TILES;
        offset++
      ) {
        const nextRow = row + offset;
        if (nextRow < 0 || nextRow >= grid.size / SURFACE_TILE_SIZE) continue;
        for (let column = 0; column < columns; column++) {
          const next = nextRow * columns + column;
          if (probed.has(next)) continue;
          probed.add(next);
          enqueue(next);
        }
      }
    }
    grid.seedTiles.forEach(enqueueSurface);
    for (let tileCursor = 0; tileCursor < tiles.length; tileCursor++) {
      const tile = tiles[tileCursor];
      let refitted = false;
      if (stopped()) break;
      pending.delete(tile);
      if (!resolved.has(tile) && !grid.covered(tile)) {
        const bounds = grid.tileBounds(tile);
        if (refinement && grid.seed.cylinder) {
          const distance =
            Math.max(
              Math.abs(bounds.row - grid.size / 2),
              Math.abs(bounds.row + SURFACE_TILE_SIZE - grid.size / 2),
            ) * grid.cellSize;
          bounds.box.expandByScalar(
            cylinderSearchMargin(grid.seed.cylinder, seed.tolerance, distance),
          );
        }
        const box = bounds.box.applyMatrix4(inverse);
        const queue = [cloud.tree.root];
        for (let nodeCursor = 0; nodeCursor < queue.length; nodeCursor++) {
          if (stopped()) break;
          const node = queue[nodeCursor];
          let spacing = Infinity;
          if (cloud.tree instanceof WSKDTree) {
            spacing = node.pointDensity / 1000;
          } else if (cloud.tree instanceof PotreeTree) {
            spacing = cloud.tree.spacing / 2 ** node.depth;
          }
          if (!node.boundingBox.intersectsBox(box)) continue;
          if (!processed.has(node.id)) {
            if (
              requested.size >= MAX_NODES ||
              points + node.numPoints > MAX_POINTS
            ) {
              partial = true;
              controller.abort();
              break;
            }
            requested.add(node.id);
            try {
              const geometry = await loadLodNode(
                client,
                node,
                controller.signal,
              );
              const positions = geometry.attributes.position;
              const { scanIds } = geometry.attributes;
              for (let index = 0; index < geometry.size; index++) {
                if (stopped()) break;
                if (++points > MAX_POINTS) {
                  partial = true;
                  controller.abort();
                  break;
                }
                if (hidden.has(scanIds?.getX(index) ?? 0)) continue;
                point
                  .fromBufferAttribute(positions, index)
                  .applyMatrix4(matrix);
                const clipped =
                  planes.length > 0 &&
                  (material.clipIntersection
                    ? planes.every((plane) => plane.distanceToPoint(point) < 0)
                    : planes.some((plane) => plane.distanceToPoint(point) < 0));
                if (!clipped) {
                  grid.addPoint(point);
                  if (refinement && grid.seed.cylinder) {
                    refinement.add(point, grid.seed.cylinder, spacing * scale);
                  }
                }
                if (index % POINTS_PER_BATCH === 0) {
                  await new Promise<void>((resolve) => setTimeout(resolve, 0));
                }
              }
              processed.add(node.id);
            } catch {
              partial = true;
              processed.add(node.id);
            } finally {
              fetcher.releaseNode(node.id, client);
            }
            if (refinement && grid.seed.cylinder && !stopped()) {
              const previousGrid = grid;
              const previousTiles = tiles;
              const cylinder = await refinement.refine(
                grid.seed.cylinder,
                stopped,
              );
              if (cylinder && !stopped()) {
                const updated = new WorldSurface({ ...seed, cylinder });
                updated.initializeCylinderPoints(
                  refinement.seedPoints(cylinder),
                );
                if (grid !== previousGrid || tiles !== previousTiles) break;
                if (!updated.seedTiles.size) partial = true;
                if (!stopped() && updated.seedTiles.size && !updated.limited) {
                  grid = updated;
                  processed.clear();
                  resolved.clear();
                  pending.clear();
                  probed.clear();
                  tiles = [];
                  grid.seedTiles.forEach(enqueueSurface);
                  refitted = true;
                  break;
                }
              }
            }
          }
          const sufficientDensity =
            node.numPoints > 0 && spacing * scale <= grid.sampleSpacing / 2;
          if (!sufficientDensity) queue.push(...node.children);
          if (nodeCursor % POINTS_PER_BATCH === 0) {
            await new Promise<void>((resolve) => setTimeout(resolve, 0));
          }
        }
        if (!refitted) resolved.add(tile);
      }
      if (refitted) {
        tileCursor = -1;
        continue;
      }
      const frontier = grid.grow(tile);
      for (const neighbor of frontier) {
        enqueueSurface(neighbor);
      }
      if (!cancelled() && performance.now() - lastUpdate >= UPDATE_INTERVAL) {
        publish(grid.selection("growing"));
        lastUpdate = performance.now();
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
    if (!cancelled()) {
      publish(grid.selection(incomplete() ? "partial" : "complete"));
    }
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener("abort", abort);
    fetcher.removeClient(client);
  }
}
